import {
    CombinedGraphQLErrors,
    ServerError,
    ServerParseError,
} from '@apollo/client/errors';
import { some } from '../util/functional/Option';
import { serverError, networkError } from '../util/Response';

/**
 * Classifies an Apollo failure into the Response that resolveCurrentScreen maps
 * onto an HTTP status code.
 *
 * Apollo Client 4 replaced ApolloError and its `networkError` property with
 * distinct classes, so the previous `error.networkError.statusCode` check would
 * now be permanently false and every failure would be reported as a network
 * error:
 *
 *   ServerError / ServerParseError -- the API answered with an HTTP status
 *   CombinedGraphQLErrors          -- the API answered 200 carrying `errors`
 *   anything else (e.g. TypeError) -- the request never completed
 *
 * The first two are server errors (500); the last is a network error.
 */
const httpStatusOf = error => {
    const isHttpError =
        error instanceof ServerError || error instanceof ServerParseError;

    return isHttpError && typeof error.statusCode === 'number'
        ? error.statusCode
        : undefined;
};

export const createError =
    ({ dataBeingFetched }) =>
    error => {
        const statusCode = httpStatusOf(error);

        if (statusCode !== undefined) {
            return serverError({
                message: `Server error (${statusCode}) fetching data for ${dataBeingFetched}`,
                error: some(error),
            });
        }

        if (error instanceof CombinedGraphQLErrors) {
            return serverError({
                message: `GraphQL API error fetching data for ${dataBeingFetched}: ${error.message}`,
                error: some(error),
            });
        }

        return networkError({
            message: `Network error fetching data from ${dataBeingFetched}`,
            error: some(error),
        });
    };
